import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';
import EditModal from './EditModal'; // Додаємо імпорт модалки
import '../../css/AdminTable.css'; // Імпортуємо стилі
import { BACKEND_URL } from '../../config';

// Компонент для редагованої комірки
const EditableCell = ({ getValue, row, column, table }) => {
    const initialValue = getValue();
    const [value, setValue] = useState(initialValue);

    // Функція для оновлення даних у стані таблиці
    const onBlur = () => {
        table.options.meta?.updateData(row.index, column.id, value);
    };

    // Оновлюємо локальний стан при зміні зовнішнього значення
    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    return (
        <input
            value={value || ''}
            onChange={e => setValue(e.target.value)}
            onBlur={onBlur} // Зберігаємо при втраті фокусу
            className="editable-cell-input"
        />
    );
};

// Основний компонент
const CategoriesAdmin = () => {
    const [data, setData] = useState([]);
    const [originalDataMap, setOriginalDataMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Додаємо стан для модального вікна, якщо його ще немає
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editModalData, setEditModalData] = useState({ value: null, rowIndex: null, columnId: null, fieldType: 'text', fieldLabel: '' });

    // Завантаження даних
    useEffect(() => {
        setLoading(true);
        fetch(`${BACKEND_URL}/categories`) // Ваш ендпоінт
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP помилка! Статус: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                const categoriesData = data || [];
                setData(categoriesData);
                setOriginalDataMap(categoriesData.reduce((map, item) => ({ ...map, [item.id]: item }), {})); // Глибоке копіювання
                setError(null);
            })
            .catch(err => {
                console.error('Помилка завантаження категорій:', err);
                setError(err.message || 'Не вдалося завантажити категорії.');
                setData([]);
                setOriginalDataMap({});
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    // Функція для відкриття модального вікна РЕДАГУВАННЯ
    const openEditModal = (cell) => {
        const { row, column } = cell;
        // Оновлюємо карту типів для відповідності новому accessorKey
        const fieldTypeMap = {
            name: 'text',
            nameSingular: 'text',
            slug: 'text',
            photo: 'textarea',
        };
        setEditModalData({
            value: cell.getValue(),
            rowIndex: row.index, 
            columnId: column.id, // column.id тут все ще буде 'photo'
            fieldType: fieldTypeMap[column.id] || 'text',
            fieldLabel: column.columnDef.header
        });
        setIsModalOpen(true);
    };

    // Функція для відкриття модального вікна СТВОРЕННЯ
    const openCreateModal = () => {
        // Налаштовуємо модалку для створення з двома полями
        setEditModalData({
            value: { name: '', nameSingular: '', slug: '', photo: '' },
            rowIndex: null,
            columnId: null,
            fieldType: 'createCategory',
            fieldLabel: 'Створити нову категорію'
        });
        setIsModalOpen(true);
    };

    // Функція СТВОРЕННЯ нової категорії (реалізація)
    const handleCreateCategory = async (categoryData) => {
        // Додаємо індикатор завантаження або блокування кнопки
        setLoading(true);
        setError(null); // Скидаємо попередні помилки
        console.log('Відправка даних для створення категорії:', categoryData);

        try {
            const authToken = localStorage.getItem('authToken');
            console.log('[handleCreateCategory] Токен, отриманий з localStorage:', authToken);
            
            const response = await fetch(`${BACKEND_URL}/categories`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Додаємо токен авторизації, якщо він є
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                body: JSON.stringify(categoryData),
            });

            if (!response.ok) {
                // Спробуємо отримати текст помилки з відповіді
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const newCategory = await response.json();
            console.log('Категорію успішно створено:', newCategory);

            // Оновлюємо стан локально ПІСЛЯ успішного створення
            setData(prevData => [...prevData, newCategory]);
            setOriginalDataMap(prevMap => ({ ...prevMap, [newCategory.id]: newCategory }));

        } catch (err) {
            console.error('Помилка створення категорії:', err);
            setError(`Помилка створення категорії: ${err.message}`);
        } finally {
            setLoading(false); // Забираємо індикатор завантаження
        }
    };

    // Функція збереження з модалки (обробляє і редагування, і створення)
    const handleModalSave = (newValue) => {
        const { rowIndex, columnId, fieldType } = editModalData;
        
        if (rowIndex === null && fieldType === 'createCategory') {
            // Створення нової категорії - newValue тепер об'єкт { name, photo }
            handleCreateCategory(newValue);
        } else if (rowIndex !== null && columnId !== null) {
            // Редагування існуючої категорії - newValue є простим значенням
            table.options.meta?.updateData(rowIndex, columnId, newValue);
        }
    };

    // Визначення колонок для react-table
    const columns = useMemo(() => [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 80,
            enableEditing: false, 
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'name',
            header: 'Назва (множина)',
            size: 220,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            accessorKey: 'nameSingular',
            header: 'Назва (однина)',
            size: 220,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            accessorKey: 'slug',
            header: 'Slug',
            size: 200,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            accessorKey: 'photo',
            header: 'Фото URL',
            size: 200,
            cell: info => <div title={info.getValue()}>{info.getValue()}</div>,
        },
        {
            id: 'actions',
            header: 'Дії',
            size: 120,
            cell: ({ row }) => {
                const originalRow = originalDataMap[row.original.id];
                const hasChanged = originalRow && JSON.stringify(originalRow) !== JSON.stringify(row.original);
                return (
                    // Додаємо клас action-buttons
                    <div className="action-buttons">
                        <button onClick={() => handleSave(row.original)} className="save-button" disabled={!hasChanged}>Зберегти</button>
                        <button onClick={() => handleDelete(row.original.id)} className="delete-button icon-button" title="Видалити">×</button>
                    </div>
                );
            },
        },
    ], [originalDataMap, openEditModal]);

    // Створення екземпляру таблиці
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 50, // 50 айтемів на сторінку
            },
        },
        meta: {
            updateData: (rowIndex, columnId, value) => {
                setData(old =>
                    old.map((row, index) => {
                        if (index === rowIndex) {
                            return { ...row, [columnId]: value };
                        }
                        return row;
                    })
                );
            },
        },
        debugTable: false, // Вимкнути дебаг в консолі
    });

    // Функція збереження змін після РЕДАГУВАННЯ (реалізація)
    const handleSave = async (rowData) => {
        setLoading(true);
        setError(null);
        console.log('Відправка даних для оновлення категорії:', rowData);

        try {
            const authToken = localStorage.getItem('authToken');
            const response = await fetch(`${BACKEND_URL}/categories/${rowData.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                },
                // Передаємо весь оновлений об'єкт категорії
                body: JSON.stringify(rowData),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
            }

            const updatedCategory = await response.json();
            console.log('Категорію успішно оновлено:', updatedCategory);

            // Оновлюємо originalDataMap ДАНИМИ З СЕРВЕРА
            setOriginalDataMap(prevMap => ({ ...prevMap, [updatedCategory.id]: updatedCategory }));
            // Оновлюємо дані в таблиці, щоб відобразити серверні дані (на випадок якщо сервер щось змінив)
            setData(prevData => prevData.map(item => item.id === updatedCategory.id ? updatedCategory : item));

        } catch (err) {
            console.error('Помилка оновлення категорії:', err);
            setError(`Помилка оновлення категорії ID ${rowData.id}: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Функція ВИДАЛЕННЯ категорії (реалізація)
    const handleDelete = async (categoryId) => {
        // Забираємо window.confirm - видалення відбувається одразу
        // if (window.confirm(`Видалити категорію ID: ${categoryId}? Це може видалити пов'язані товари!`)) { 
            setLoading(true);
            setError(null);
            console.log('Запит на видалення категорії ID:', categoryId);

            try {
                const authToken = localStorage.getItem('authToken');
                const response = await fetch(`${BACKEND_URL}/categories/${categoryId}`, {
                    method: 'DELETE',
                    headers: {
                        ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
                    },
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP помилка! Статус: ${response.status}, Текст: ${errorText || 'N/A'}`);
                }

                console.log('Категорію успішно видалено ID:', categoryId);

                // Оновлюємо стан ПІСЛЯ успішного видалення
                setData(prevData => prevData.filter(cat => cat.id !== categoryId));
                setOriginalDataMap(prevMap => {
                    const { [categoryId]: _, ...rest } = prevMap;
                    return rest;
                });
                // Можна прибрати alert, якщо він не потрібен
                // alert('Категорію успішно видалено!'); 

            } catch (err) {
                console.error('Помилка видалення категорії:', err);
                setError(`Помилка видалення категорії ID ${categoryId}: ${err.message}`);
                alert(`Помилка видалення категорії: ${err.message}`);
            } finally {
                setLoading(false);
            }
        // }
    };

    if (loading) return <div>Завантаження категорій...</div>;
    if (error) return <div className="error-message">Помилка: {error}</div>;

    return (
        <div>
            <h2>Керування Категоріями</h2>

            {/* Кнопка для створення нової категорії */} 
            <div style={{ marginBottom: '15px' }}>
                <button onClick={openCreateModal} className="create-button"> {/* Можна додати стилі для .create-button */} 
                    + Створити нову категорію
                </button>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th key={header.id} style={{ width: header.getSize() ? `${header.getSize()}px` : undefined }}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                                    Немає категорій.
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map(row => (
                                <tr key={row.id}>
                                    {row.getVisibleCells().map(cell => {
                                        // Визначаємо, чи є комірка редагованою (не ID і не Дії)
                                        const isEditable = cell.column.id !== 'id' && cell.column.id !== 'actions';
                                        return (
                                            <td
                                                key={cell.id}
                                                style={{ width: cell.column.getSize() ? `${cell.column.getSize()}px` : undefined }}
                                                // Додаємо onClick ТІЛЬКИ до редагованих комірок
                                                onClick={isEditable ? () => openEditModal(cell.getContext()) : undefined}
                                                // Додаємо клас ТІЛЬКИ до редагованих комірок
                                                className={isEditable ? 'editable-td' : ''}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Модальне вікно (використовується і для редагування, і для створення) */} 
            <EditModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                initialValue={editModalData.value} // Початкове значення (порожнє для створення)
                onSave={handleModalSave} // Функція збереження
                fieldType={editModalData.fieldType} // Тип поля
                fieldLabel={editModalData.fieldLabel} // Заголовок
            />

            {/* Пагінація */}
            <div className="pagination-controls">
                <button
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                >
                    {'<<'}
                </button>
                <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                >
                    {'<'}
                </button>
                <span>
                    Сторінка{' '}
                    <strong>
                        {table.getState().pagination.pageIndex + 1} з {table.getPageCount()}
                    </strong>{' '}
                </span>
                <input
                     type="number"
                     defaultValue={table.getState().pagination.pageIndex + 1}
                     onChange={e => {
                         const page = e.target.value ? Number(e.target.value) - 1 : 0;
                         if (page >= 0 && page < table.getPageCount()) {
                           table.setPageIndex(page);
                         }
                     }}
                     style={{ width: '60px', margin: '0 5px' }}
                     min={1}
                     max={table.getPageCount()}
                />
                <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                >
                    {'>'}
                </button>
                 <button
                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                    disabled={!table.getCanNextPage()}
                >
                    {'>>'}
                </button>

                <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                        table.setPageSize(Number(e.target.value));
                    }}
                    style={{ marginLeft: '10px' }}
                >
                    {[10, 20, 30, 40, 50, 100].map(pageSize => (
                        <option key={pageSize} value={pageSize}>
                            Показати {pageSize}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default CategoriesAdmin; 